/**
 * Pre-submit review model for Manual Deal Entry.
 *
 * Pure: turns the form's values into the rows the confirmation screen prints.
 * Tags are Entered / Defaulted / Calculated. Defaults are the platform
 * assumptions (`intakeDefaults`); calculated cells come from the existing
 * `deriveCatalogue` rules — no new derivation direction.
 */
import { deriveCatalogue } from '@/components/deal/dossier/deriveCatalogue'
import { formatMoney, formatPoints } from '@/components/deal/dossier/format'
import { moneyWithCode } from '@/pages/pipeline-intelligence/currency'
import { parseMoneyText, type DealFieldDef } from '@/services/dealFile'
import { formatDeskDate } from '@/utils/dates'
import { assumedValue, DEFAULT_CURRENCY } from './intakeDefaults'
import {
  CALCULATED_READONLY_FIELDS,
  MANUAL_ENTRY_SECTION_LABELS,
  MANUAL_ENTRY_SECTION_ORDER,
  fieldsInSection,
  hideOnForm,
  type ManualEntrySectionId,
} from './manualEntryLayout'
import { blocksCreate, MANUAL_ENTRY_DEFAULTS, offeredTotal } from './manualEntryRules'

export type ReviewTag = 'entered' | 'defaulted' | 'calculated'

export interface ReviewRow {
  field: string
  label: string
  value: string
  tag: ReviewTag
  formula?: string
}

export interface ReviewSection {
  id: ManualEntrySectionId
  label: string
  rows: ReviewRow[]
}

export interface ReviewModel {
  sections: ReviewSection[]
  backshopBlank: string[]
}

function formatReviewMoney(amount: number, fallback: string, currency: string): string {
  const formatted = formatMoney(amount, fallback)
  const code = currency.trim().toUpperCase() || DEFAULT_CURRENCY
  return code === DEFAULT_CURRENCY ? formatted : moneyWithCode(formatted, code)
}

function formatReviewValue(def: DealFieldDef | undefined, raw: string, currency: string): string {
  const text = raw.trim()
  if (!text) return ''
  if (!def) return text
  if (def.kind === 'currency') {
    const amount = parseMoneyText(text)
    return amount === null ? text : formatReviewMoney(amount, text, currency)
  }
  if (def.kind === 'percent') {
    const n = Number(text.replace(/[%\s,]/g, ''))
    return Number.isFinite(n) ? formatPoints(n, text) : text
  }
  if (def.kind === 'date') return formatDeskDate(text) || text
  return text
}

function formatDerived(def: DealFieldDef | undefined, n: number, currency: string): string {
  if (def?.kind === 'percent') return formatPoints(n)
  if (def?.kind === 'currency') return formatReviewMoney(n, String(n), currency)
  if (Number.isInteger(n)) return String(n)
  return n.toFixed(2)
}

function defaultLabel(field: string, assumed: string): string {
  if (field === 'unit_of_measure') return `Default from Property Type`
  if (field === 'position') return `Default: ${assumed}`
  if (field === 'currency') return `Default: ${assumed}`
  if (field === 'assignee') return `Default: ${assumed}`
  if (field === 'originated') return 'Default: desk date'
  if (field === 'stage') return 'Default: Triage'
  if (field === 'fully_funded_amount') return 'Default: initial + future'
  if (field === 'capital_program') return `Default: ${assumed}`
  if (field === 'rate_type') return `Default: ${assumed}`
  if (field === 'payment_frequency') return 'Default: Monthly (in arrears)'
  return `Default: ${assumed}`
}

export function buildReviewModel(
  fields: DealFieldDef[],
  values: Record<string, string>,
  owned: Record<string, boolean>,
  isAdmin: boolean,
): ReviewModel {
  const stated = Object.keys(owned).filter((name) => owned[name] && (values[name] || '').trim())
  const derived = deriveCatalogue(values, stated)
  const currency = (values.currency || '').trim() || assumedValue('currency') || DEFAULT_CURRENCY
  const sections: ReviewSection[] = []

  for (const id of MANUAL_ENTRY_SECTION_ORDER) {
    const rows: ReviewRow[] = []
    for (const def of fieldsInSection(fields, id)) {
      if (hideOnForm(def.name, values, isAdmin)) continue
      const raw = (values[def.name] || '').trim()
      const personKeyed = Boolean(owned[def.name] && raw)

      if (CALCULATED_READONLY_FIELDS.has(def.name)) {
        const n = derived.derived[def.name]
        if (n === undefined) continue
        rows.push({
          field: def.name,
          label: def.label,
          value: formatDerived(def, n, currency),
          tag: 'calculated',
          formula: derived.notes[def.name],
        })
        continue
      }

      if (personKeyed) {
        rows.push({
          field: def.name,
          label: def.label,
          value: formatReviewValue(def, raw, currency),
          tag: 'entered',
        })
        continue
      }

      const assumed = assumedValue(def.name, values.asset_type)
      if (assumed && !raw) {
        rows.push({
          field: def.name,
          label: def.label,
          value: assumed,
          tag: 'defaulted',
          formula: defaultLabel(def.name, assumed),
        })
        continue
      }

      if (def.name === 'fully_funded_amount' && !owned[def.name]) {
        const offered = offeredTotal(values)
        const offeredAmount = offered ? parseMoneyText(offered) : null
        const rawAmount = raw ? parseMoneyText(raw) : null
        const matchesOffer =
          offeredAmount !== null && rawAmount !== null && offeredAmount === rawAmount
        if (offered && (!raw || matchesOffer)) {
          rows.push({
            field: def.name,
            label: def.label,
            value: formatReviewValue(def, raw || offered, currency),
            tag: 'defaulted',
            formula: defaultLabel(def.name, offered),
          })
          continue
        }
      }

      if (def.name in MANUAL_ENTRY_DEFAULTS && raw && !owned[def.name]) {
        rows.push({
          field: def.name,
          label: def.label,
          value: formatReviewValue(def, raw, currency),
          tag: 'defaulted',
          formula: defaultLabel(def.name, raw),
        })
        continue
      }

      if (def.name === 'originated' && raw && !owned[def.name]) {
        rows.push({
          field: def.name,
          label: def.label,
          value: formatReviewValue(def, raw, currency),
          tag: 'defaulted',
          formula: defaultLabel(def.name, raw),
        })
        continue
      }

      if (!raw && derived.derived[def.name] !== undefined) {
        rows.push({
          field: def.name,
          label: def.label,
          value: formatDerived(def, derived.derived[def.name], currency),
          tag: 'calculated',
          formula: derived.notes[def.name],
        })
        continue
      }

      if (raw) {
        rows.push({
          field: def.name,
          label: def.label,
          value: formatReviewValue(def, raw, currency),
          tag: 'entered',
        })
      }
    }
    if (rows.length) {
      sections.push({ id, label: MANUAL_ENTRY_SECTION_LABELS[id], rows })
    }
  }

  const shown = new Set(sections.flatMap((section) => section.rows.map((row) => row.field)))
  const backshopBlank = fields
    .filter((f) => f.required && !blocksCreate(f) && !shown.has(f.name))
    .filter((f) => !assumedValue(f.name, values.asset_type) && !(values[f.name] || '').trim())
    .map((f) => f.label)

  return { sections, backshopBlank }
}

export function reviewTagLabel(tag: ReviewTag): string {
  switch (tag) {
    case 'entered':
      return 'Entered'
    case 'defaulted':
      return 'Defaulted'
    case 'calculated':
      return 'Calculated'
    default: {
      const _exhaustive: never = tag
      return _exhaustive
    }
  }
}

/** Fields the person keyed — the ones the confirmation endpoint should approve. */
export function keyedFieldNames(
  values: Record<string, string>,
  owned: Record<string, boolean>,
  isAdmin = false,
): string[] {
  return Object.keys(owned).filter(
    (name) =>
      owned[name] &&
      (values[name] || '').trim() &&
      !CALCULATED_READONLY_FIELDS.has(name) &&
      !hideOnForm(name, values, isAdmin),
  )
}
